import React from "react";
import { Users, UserPlus } from "lucide-react";
import { Card } from "../../common/Primitives";

/**
 * Reviewer workload rebalance panel. Shows open queue count, SLA breaches
 * and oldest ticket age per reviewer, with a one-click reassign of the
 * currently-active KYC to that reviewer.
 *
 * Data: GET /admin/kyc/workload
 */
export default function WorkloadPanel({ workload, onReassign, activeId, team }) {
  const reviewers = workload?.reviewers || [];
  const unassigned = workload?.unassigned_under_review || 0;
  return (
    <Card testId="kyc-workload-card" className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="overline flex items-center gap-1.5"><Users size={12} /> Reviewer Workload · rebalance</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Live open queue per team member. Reassign the current KYC from the detail pane.
          </div>
        </div>
        {unassigned > 0 && (
          <span className="text-[11px] font-heading uppercase tracking-widest px-2 py-1 rounded-full bg-rose-100 border border-rose-300 text-rose-800" data-testid="kyc-unassigned-count">
            {unassigned} unassigned
          </span>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
        {reviewers.map((r) => {
          const hot = r.sla_breaches > 0;
          const meIsActive = !!activeId && r.user_id;
          return (
            <div
              key={r.user_id}
              className={`p-3 rounded-lg border ${hot ? "border-rose-300 bg-rose-50" : "border-navy/[0.08] bg-white"}`}
              data-testid={`kyc-workload-${r.user_id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="font-heading text-ink text-sm truncate">{r.name}</div>
                <span className="text-[10px] font-mono text-ink-muted">{r.role}</span>
              </div>
              <div className="text-[11px] font-mono text-ink-muted truncate">{r.email}</div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-center text-xs">
                <div>
                  <div className={`font-heading text-lg ${hot ? "text-rose-800" : "text-ink"}`}>{r.under_review}</div>
                  <div className="text-[9px] uppercase tracking-widest text-ink-muted">Open</div>
                </div>
                <div>
                  <div className={`font-heading text-lg ${r.sla_breaches ? "text-rose-800" : "text-ink-muted"}`}>{r.sla_breaches}</div>
                  <div className="text-[9px] uppercase tracking-widest text-ink-muted">Breach</div>
                </div>
                <div>
                  <div className={`font-heading text-lg ${r.oldest_hours >= workload.sla_hours ? "text-rose-800" : "text-ink"}`}>
                    {r.oldest_hours || 0}h
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-ink-muted">Oldest</div>
                </div>
              </div>
              {meIsActive && team.length > 0 && (
                <button
                  onClick={() => onReassign(activeId, r.user_id)}
                  className="mt-3 w-full px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center justify-center gap-1"
                  data-testid={`kyc-assign-active-to-${r.user_id}`}
                >
                  <UserPlus size={11} /> Assign current KYC
                </button>
              )}
            </div>
          );
        })}
        {reviewers.length === 0 && (
          <div className="col-span-full text-xs text-ink-muted italic">No team members loaded.</div>
        )}
      </div>
    </Card>
  );
}
